import type { ProjectMixProjectRow, ProjectMixStats } from '../types/projectAnalysis'
import {
  buildFYOptions,
  getCurrentFYStartYear,
  getMonthsForFYQ,
  MONTH_NAMES,
} from './projectAnalysisPeriod'

export type OrgFilterMode = 'fy' | 'calendar'

export interface OverviewFilterState {
  orgFilterMode: OrgFilterMode
  orgFY: string
  orgQ: string
  orgYear: string
  orgMonth: string
  orgCategoryFilter: string
}

export function computeOrgPeriodKeys(s: OverviewFilterState): string[] | null {
  if (s.orgFilterMode === 'fy' && s.orgFY && s.orgFY !== 'all') {
    const months = getMonthsForFYQ(s.orgFY, s.orgQ)
    return months
      ? months.map(
          ({ year, month }) =>
            `${year}-${String(month).padStart(2, '0')}`,
        )
      : []
  }
  if (s.orgFilterMode === 'calendar' && s.orgYear && s.orgYear !== 'all') {
    if (s.orgMonth && s.orgMonth !== 'all')
      return [`${s.orgYear}-${String(s.orgMonth).padStart(2, '0')}`]
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
      (m) => `${s.orgYear}-${String(m).padStart(2, '0')}`,
    )
  }
  return null
}

export function computeOrgYearOptions(projectMixStats: ProjectMixStats | null): number[] {
  if (!projectMixStats?.by_year_month) return []
  const keys = Object.keys(projectMixStats.by_year_month)
  return [
    ...new Set(keys.map((k) => k.split('-')[0]).filter(Boolean)),
  ]
    .map(Number)
    .sort((a, b) => b - a)
}

export function computeOrgFYOptions(orgYearOptions: number[]) {
  const opts = buildFYOptions(
    orgYearOptions.length ? orgYearOptions : [new Date().getFullYear()],
    getCurrentFYStartYear(),
  )
  if (opts.length) return opts
  const y = getCurrentFYStartYear()
  return [
    {
      value: `${y}-${String(y + 1).slice(-2)}`,
      label: `FY ${y}-${String(y + 1).slice(-2)}`,
    },
  ]
}

export function computeProjectList(
  projectMixStats: ProjectMixStats | null,
): ProjectMixProjectRow[] {
  if (!projectMixStats) return []
  const all = projectMixStats.all_projects
  if (Array.isArray(all) && all.length > 0) return all
  const top = projectMixStats.top_projects_by_billable || []
  return top.map((p) => ({
    project_id: p.project_id,
    project_name: p.project_name,
    total_hours: p.total_hours ?? 0,
    billable_hours: p.billable_hours ?? 0,
    non_billable_hours: (p.total_hours ?? 0) - (p.billable_hours ?? 0),
    utilization_pct: p.utilization_pct ?? 0,
    entries_count: 0,
    late_entries: p.late_entries ?? 0,
    pending_signoffs: p.pending_signoffs ?? 0,
    leave_days: p.leave_days ?? 0,
    project_category: null,
    is_poc_internal: p.is_poc_internal ?? false,
  }))
}

export function computeProjectListForDisplay(
  projectMixStats: ProjectMixStats | null,
  orgPeriodKeys: string[] | null,
  projectList: ProjectMixProjectRow[],
): ProjectMixProjectRow[] {
  if (!projectMixStats?.by_year_month_projects || !orgPeriodKeys?.length)
    return projectList
  const byYmProj = projectMixStats.by_year_month_projects
  type MergedAcc = {
    project_id: number
    project_name?: string | null
    project_category: string | null
    total_hours: number
    billable_hours: number
    non_billable_hours: number
    entries_count: number
    late_entries: number
    pending_signoffs: number
    leave_days: number
  }
  const merged = new Map<number, MergedAcc>()
  orgPeriodKeys.forEach((key) => {
    const arr = byYmProj[key]
    if (!Array.isArray(arr)) return
    arr.forEach((p) => {
      const id = p.project_id
      if (!merged.has(id)) {
        merged.set(id, {
          project_id: id,
          project_name: p.project_name,
          project_category: p.project_category ?? null,
          total_hours: 0,
          billable_hours: 0,
          non_billable_hours: 0,
          entries_count: 0,
          late_entries: 0,
          pending_signoffs: 0,
          leave_days: 0,
        })
      }
      const m = merged.get(id)!
      m.total_hours += Number(p.total_hours) || 0
      m.billable_hours += Number(p.billable_hours) || 0
      m.non_billable_hours += Number(p.non_billable_hours) || 0
      m.entries_count += Number(p.entries_count) || 0
      m.late_entries += Number(p.late_entries) || 0
      m.pending_signoffs += Number(p.pending_signoffs) || 0
      m.leave_days += Number(p.leave_days) || 0
    })
  })
  return Array.from(merged.values())
    .map((p) => ({
      ...p,
      total_hours: Math.round(Number(p.total_hours) * 100) / 100,
      billable_hours: Math.round(Number(p.billable_hours) * 100) / 100,
      non_billable_hours: Math.round(Number(p.non_billable_hours) * 100) / 100,
      leave_days: Math.round((Number(p.leave_days) || 0) * 10) / 10,
      utilization_pct:
        (p.total_hours ?? 0) > 0
          ? Math.round(
              ((Number(p.billable_hours) || 0) / (Number(p.total_hours) || 1)) *
                1000,
            ) / 10
          : 0,
    }))
    .sort((a, b) => (b.total_hours || 0) - (a.total_hours || 0))
}

export function computeCategoryOptions(
  projectList: ProjectMixProjectRow[],
): string[] {
  const set = new Set<string>()
  projectList.forEach((p) => {
    const cat = (p.project_category || '').toString().trim()
    if (cat) set.add(cat)
  })
  return [
    '',
    ...Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    ),
  ]
}

export function computeCategoryFilteredList(
  projectListForDisplay: ProjectMixProjectRow[],
  orgCategoryFilter: string,
): ProjectMixProjectRow[] {
  if (!orgCategoryFilter) return projectListForDisplay
  const want = orgCategoryFilter.toLowerCase()
  return projectListForDisplay.filter(
    (p) => (p.project_category || '').toString().toLowerCase() === want,
  )
}

export type ProjectSortKey =
  | 'total_hours'
  | 'billable_hours'
  | 'utilization_pct'
  | 'project_name'
  | 'project_id'

export function filterAndSortProjects(
  list: ProjectMixProjectRow[],
  search: string,
  sortBy: ProjectSortKey,
  sortOrder: 'asc' | 'desc',
): ProjectMixProjectRow[] {
  let out = [...list]
  const term = (search || '').toLowerCase().trim()
  if (term) {
    out = out.filter(
      (p) =>
        (p.project_name || '').toLowerCase().includes(term) ||
        String(p.project_id || '').includes(term) ||
        (p.client_name || '').toLowerCase().includes(term) ||
        (p.project_category || '').toLowerCase().includes(term),
    )
  }
  const mult = sortOrder === 'asc' ? 1 : -1
  out.sort((a, b) => {
    const va = a[sortBy as keyof ProjectMixProjectRow]
    const vb = b[sortBy as keyof ProjectMixProjectRow]
    if (typeof va === 'number' && typeof vb === 'number')
      return mult * (va - vb)
    return mult * String(va || '').localeCompare(String(vb || ''))
  })
  return out
}

export { MONTH_NAMES }
